import CommentsView from 'enums/comments/views';
import { wccModules } from 'enums/wccModules';
import { CustomElementConfig } from 'extensions/knockout/bindings/customElement';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { ActivityFormEditorConfig } from 'managers/topic/form';
import TaskActivityViewType from './enums/viewType';
import './viewlink.html';
import './viewlink.scss';

export interface TaskActivityViewLinkConfig {
    title: string
    type: TaskActivityViewType
    sidebar?: SubscribableOrNullableValue<boolean>
    commentsView?: SubscribableOrNullableValue<CommentsView>    
    mainForm?: SubscribableOrNullableValue<ActivityFormEditorConfig>
    secondaryForm?: SubscribableOrNullableValue<ActivityFormEditorConfig>
    customForm?: SubscribableOrNullableValue<CustomElementConfig>
    todo?: SubscribableOrNullableValue<number> | SubscribableOrNullableValue<boolean>
}

export class TaskActivityViewLinkModel {
    isActive = ko.observable(false);

    title: Subscribable<string>
    type: Subscribable<TaskActivityViewType>
    sidebar: Subscribable<boolean>
    commentsView: Subscribable<CommentsView | undefined>
    mainForm: Subscribable<ActivityFormEditorConfig | undefined>
    secondaryForm: Subscribable<ActivityFormEditorConfig | undefined>
    customForm: Subscribable<CustomElementConfig | undefined>
    todo: Subscribable<number | boolean>

    constructor(config: TaskActivityViewLinkConfig) {
        this.type = ko.flattenComputed(config.type);
        this.title = ko.flattenComputed(config.title);
        this.sidebar = ko.flattenComputed(config.sidebar, true);
        this.commentsView = ko.flattenComputed(config.commentsView);
        this.mainForm = ko.flattenComputed(config.mainForm);
        this.secondaryForm = ko.flattenComputed(config.secondaryForm);
        this.customForm = ko.flattenComputed(config.customForm);
        this.todo = ko.flattenComputed(config.todo as SubscribableOrNullableValue<number | boolean>, 0);
    }
}

@injectable()
export default class TaskActivityViewLinkVM {
    title: Subscribable<string>
    toDoCount: Subscribable<number>

    isActive: Observable<boolean>
    isInactive: Subscribable<boolean>
    todoShown: Subscribable<boolean>

    isMaterialUIEnabled: PureComputed<boolean>

    constructor(
        @inject(wccModules.componentConfig) model: TaskActivityViewLinkModel,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        this.title = model.title;
        const type = model.type;
        this.isActive = model.isActive;
        this.isInactive = this.isActive.not();

        this.toDoCount = model.todo.mapSingle(value => this.processToDo(value));
        this.todoShown = this.toDoCount.is(v => v > 0);

        this.isMaterialUIEnabled = ko.pureComputed(() => WCC.Settings.MaterialUIEnabled ?? false);

        const isUnreadLink = type.is(TaskActivityViewType.unread);
        const isFeatured = ko.pureComputed(() => isUnreadLink() && this.todoShown());

        ko.applyBindingsToNode(info.element, {
            'click': () => this.isActive(true),

            'css': {
                'task-activity__viewlink': true,
                'task-activity__viewlink--featured': isFeatured,
                'task-activity__viewlink--with-badge': this.todoShown
            }
        });
    }

    private processToDo(value: number | boolean) {
        if (value === true || value === false) {
            return value ? 1 : 0;
        } else {
            return value;
        }
    }
}